import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { visibleFor } from "@/lib/helpContent";
import { PageHeader, Card, EmptyState } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, GraduationCap, ListChecks, HelpCircle, CheckCircle2, ArrowRight, Route, LogIn, Target, Rocket, MapPin, Sparkles } from "lucide-react";
import { walkthroughFor } from "@/lib/walkthrough";
import { changelogFor } from "@/lib/changelog";
import { quickstartFor, linkForTask } from "@/lib/quickstart";
import { CC_ROLES } from "@/lib/constants";

export default function HelpCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const content = useMemo(() => visibleFor(user.role), [user.role]);
  const quick = useMemo(() => quickstartFor(user.role), [user.role]);
  const term = q.trim().toLowerCase();
  const showWalkthrough = CC_ROLES.includes(user.role) || user.role === "DOR";
  const wt = useMemo(() => walkthroughFor(user.role), [user.role]);
  const wantsWalkthrough = new URLSearchParams(window.location.search).get("tab") === "walkthrough";

  const match = (...parts) => !term || parts.filter(Boolean).join(" ").toLowerCase().includes(term);

  const quickTasks = (quick?.tasks || []).filter((t) => match(t.q, t.where, (t.steps || []).join(" ")));
  const training = content.training.filter((t) => match(t.role, t.tagline, t.purpose, t.responsibilities.join(" "), t.dayInLife.join(" ")));
  const workflows = content.workflows.filter((w) => match(w.title, w.audience, w.steps.join(" ")));
  const faqs = content.faqs.filter((f) => match(f.q, f.a, f.category));
  const news = useMemo(() => changelogFor(user.role), [user.role]).filter((c) => match(c.title, c.desc));
  const total = quickTasks.length + training.length + workflows.length + faqs.length;

  return (
    <div>
      <PageHeader title="Help Center" subtitle={`How-to guides, workflows & FAQs for your role (${user.role})`} />

      <div className="relative mb-6 max-w-2xl">
        <Search className="w-5 h-5 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          data-testid="help-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search everything, e.g. 'reset password', 'decline', 'paperwork', 'video call'…"
          className="pl-11 h-12 text-base"
        />
      </div>

      {term && <p className="text-sm text-zinc-500 mb-4" data-testid="help-results-count">{total} result{total === 1 ? "" : "s"} for “{q}”</p>}

      {(CC_ROLES.includes(user.role) || user.role === "Super Admin") && (
        <Card className="mb-6 border-cc-orange/30 bg-orange-50/60" data-testid="trainer-guide-card">
          <div className="flex flex-wrap items-center justify-between gap-4 p-1">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-cc-orange" />Trainer Guide &amp; Handouts
              </h3>
              <p className="text-sm text-zinc-600 mt-1">
                Everything you need to train a facility or a school live — timed agendas, click-by-click
                walkthroughs, FAQs and four printable handouts (DOR, Clinical Instructor, School contact,
                First 7 days). Opens in a new tab; use its Print button to save as PDF.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <a href="/training-guide.html" target="_blank" rel="noreferrer">
                <Button className="bg-cc-orange hover:bg-orange-600 text-white" data-testid="trainer-guide-btn">
                  Open trainer guide<ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </a>
              <a href="/student-welcome-guide.html" target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="student-guide-btn">Student welcome guide</Button>
              </a>
            </div>
          </div>
        </Card>
      )}

      <Tabs defaultValue={showWalkthrough && (wantsWalkthrough || !quick) ? "walkthrough" : (quick ? "quickstart" : "training")}>
        <TabsList className="mb-5 flex-wrap h-auto">
          {quick && <TabsTrigger value="quickstart" data-testid="help-tab-quickstart"><Rocket className="w-4 h-4 mr-1.5" />Getting started</TabsTrigger>}
          {showWalkthrough && <TabsTrigger value="walkthrough" data-testid="help-tab-walkthrough"><Route className="w-4 h-4 mr-1.5" />Walkthrough</TabsTrigger>}
          <TabsTrigger value="training" data-testid="help-tab-training"><GraduationCap className="w-4 h-4 mr-1.5" />Roles</TabsTrigger>
          <TabsTrigger value="workflows" data-testid="help-tab-workflows"><ListChecks className="w-4 h-4 mr-1.5" />Workflows</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="help-tab-faqs"><HelpCircle className="w-4 h-4 mr-1.5" />FAQs</TabsTrigger>
          <TabsTrigger value="whatsnew" data-testid="help-tab-whatsnew"><Sparkles className="w-4 h-4 mr-1.5" />What's new</TabsTrigger>
        </TabsList>

        {/* QUICKSTART — per role */}
        {quick && (
          <TabsContent value="quickstart">
            <Card className="mb-5 border-cc-blue/20 bg-cc-blue/5">
              <div className="p-1" data-testid="quickstart-intro">
                <h3 className="text-base font-semibold text-zinc-900 flex items-center gap-2"><Rocket className="w-4 h-4 text-cc-blue" />Getting started as a {user.role}</h3>
                <p className="text-sm text-zinc-600 mt-2 block">{quick.intro}</p>
              </div>
            </Card>
            {quickTasks.length === 0 ? <Card><EmptyState text="No tasks match your search." /></Card> : (
              <Accordion type="single" collapsible className="w-full space-y-3">
                {quickTasks.map((t, i) => (
                  <Card key={i} className="p-0">
                    <AccordionItem value={`qs-${i}`} className="border-0" data-testid={`help-quickstart-${i}`}>
                      <AccordionTrigger className="text-left px-5 py-4 hover:no-underline">
                        <span className="font-medium text-zinc-900">{t.q}</span>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-5">
                        <p className="flex items-center gap-1.5 text-xs text-cc-blue font-medium mb-3"><MapPin className="w-3.5 h-3.5" />{t.where}</p>
                        <ol className="space-y-2.5">
                          {t.steps.map((s, j) => (
                            <li key={j} className="flex items-start gap-3 text-sm text-zinc-700">
                              <span className="w-5 h-5 shrink-0 rounded-full bg-cc-blue text-white text-xs font-bold flex items-center justify-center">{j + 1}</span>
                              <span className="pt-0.5">{s}</span>
                            </li>
                          ))}
                        </ol>
                        {linkForTask(t) && (
                          <button onClick={() => navigate(linkForTask(t))} data-testid={`help-quickstart-link-${i}`}
                            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-cc-blue hover:underline">
                            Open {t.where.split("→")[0].trim().replace(/\s*\(.*\)/, "")} <ArrowRight className="w-4 h-4" />
                          </button>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Card>
                ))}
              </Accordion>
            )}
          </TabsContent>
        )}

        {/* WALKTHROUGH */}
        {showWalkthrough && (
          <TabsContent value="walkthrough">
            <Card className="mb-5 border-cc-blue/20 bg-cc-blue/5">
              <div className="p-1" data-testid="walkthrough-intro">
                <h3 className="text-base font-semibold text-zinc-900 flex items-center gap-2"><Route className="w-4 h-4 text-cc-blue" />{user.role === "DOR" ? "Your walkthrough as a Director of Rehab" : "Full end-to-end walkthrough"}</h3>
                <p className="text-sm text-zinc-600 mt-2">{wt.intro}</p>
              </div>
            </Card>
            <div className="space-y-4">
              {wt.steps.map((s) => (
                <Card key={s.id} className="p-0">
                  <div className="p-6" data-testid={`walkthrough-${s.id}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-cc-blue bg-cc-blue/10 px-2 py-0.5 rounded">{s.role}</span>
                      <span className="text-xs text-zinc-400">{s.page}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-zinc-900 mt-1">{s.title}</h3>
                    <p className="flex items-center gap-1.5 text-xs text-zinc-500 mt-2"><LogIn className="w-3.5 h-3.5" />Log in as: <span className="font-mono text-zinc-700">{s.login}</span></p>
                    <ol className="mt-4 space-y-3">
                      {s.steps.map((st, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-zinc-700">
                          <span className="w-6 h-6 shrink-0 rounded-full bg-cc-blue text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                          <span className="pt-0.5">{st}</span>
                        </li>
                      ))}
                    </ol>
                    <div className="mt-4 flex items-start gap-2 rounded-md bg-cc-green/10 border border-cc-green/20 px-3 py-2">
                      <Target className="w-4 h-4 text-cc-green mt-0.5 shrink-0" />
                      <p className="text-sm text-zinc-700"><span className="font-semibold">Expected:</span> {s.expect}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}

        {/* TRAINING */}
        <TabsContent value="training">
          {training.length === 0 ? <Card><EmptyState text="No training matches your search." /></Card> : (
            <div className="grid md:grid-cols-2 gap-5">
              {training.map((t) => (
                <Card key={t.id} className="p-0" >
                  <div className="p-6" data-testid={`help-training-${t.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-cc-blue bg-cc-blue/10 px-2 py-0.5 rounded">{t.role}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-zinc-900 mt-2">What does a {t.role} do?</h3>
                    <p className="text-sm text-zinc-500 mt-1">{t.tagline}</p>
                    <p className="text-sm text-zinc-700 mt-3">{t.purpose}</p>

                    <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mt-5 mb-2">Key responsibilities</p>
                    <ul className="space-y-1.5">
                      {t.responsibilities.map((r, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                          <CheckCircle2 className="w-4 h-4 text-cc-green mt-0.5 shrink-0" />{r}
                        </li>
                      ))}
                    </ul>

                    <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mt-5 mb-2">A day in the system</p>
                    <ol className="space-y-2">
                      {t.dayInLife.map((s, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-zinc-700">
                          <span className="w-5 h-5 shrink-0 rounded-full bg-cc-orange/15 text-cc-orange text-xs font-bold flex items-center justify-center">{i + 1}</span>
                          {s}
                        </li>
                      ))}
                    </ol>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* WORKFLOWS */}
        <TabsContent value="workflows">
          {workflows.length === 0 ? <Card><EmptyState text="No workflows match your search." /></Card> : (
            <div className="grid md:grid-cols-2 gap-5">
              {workflows.map((w) => (
                <Card key={w.id} className="p-0">
                  <div className="p-6" data-testid={`help-workflow-${w.id}`}>
                    <h3 className="text-base font-semibold text-zinc-900">{w.title}</h3>
                    <p className="text-xs text-zinc-400 mt-0.5">For: {w.audience}</p>
                    <ol className="mt-4 space-y-3">
                      {w.steps.map((s, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-zinc-700">
                          <span className="w-6 h-6 shrink-0 rounded-full bg-cc-blue text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                          <span className="pt-0.5">{s}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* FAQS */}
        <TabsContent value="faqs">
          {faqs.length === 0 ? <Card><EmptyState text="No FAQs match your search." /></Card> : (
            <Card>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f) => (
                  <AccordionItem key={f.id} value={f.id} data-testid={`help-faq-${f.id}`}>
                    <AccordionTrigger className="text-left">
                      <span className="flex items-center gap-3">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded shrink-0">{f.category}</span>
                        {f.q}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-zinc-600">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Card>
          )}
        </TabsContent>

        {/* WHAT'S NEW */}
        <TabsContent value="whatsnew">
          <Card className="mb-5 border-cc-blue/20 bg-cc-blue/5">
            <div className="p-1" data-testid="whatsnew-intro">
              <h3 className="text-base font-semibold text-zinc-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-cc-blue" />What's new in Campus Connections</h3>
              <p className="text-sm text-zinc-600 mt-2">Everything shipped recently that affects you as a {user.role}, newest first.</p>
            </div>
          </Card>
          {news.length === 0 ? <Card><EmptyState text="No updates match your search." /></Card> : (
            <div className="space-y-4">
              {news.map((c, i) => (
                <Card key={c.id} className="p-0">
                  <div className="p-6" data-testid={`help-whatsnew-${c.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      {i === 0 && <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-cc-orange px-2 py-0.5 rounded-full">Latest</span>}
                      <span className="text-xs text-zinc-400">{c.date}</span>
                    </div>
                    <h3 className="text-base font-semibold text-zinc-900 mt-1.5">{c.title}</h3>
                    <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{c.desc}</p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="mt-8 flex items-center gap-2 text-sm text-zinc-500">
        <ArrowRight className="w-4 h-4" />
        Still stuck? Use the Messages on any placement to reach your Campus Coordinator.
      </div>
    </div>
  );
}
